import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Tooltip, Progress } from 'antd';
import { EyeOutlined, CheckCircleOutlined, ClockCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import LibraryPage from '../../../components/layout/LibraryPage';
import { TGDataTable, StatusTag } from '../../../components/common';
import SubmittedDateFilterBar from './SubmittedDateFilterBar';
import { useGetAllResponsesQuery } from '../../../store/api/templateApi';
import { formatDate } from '../../../utils/dateUtils';

export default function CompletedList({ embedded = false }) {
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState(null);

  const { data: response, isLoading } = useGetAllResponsesQuery();

  const responses = response?.data || [];

  // Filter responses by submitted date
  const filteredResponses = useMemo(() => {
    if (!dateFilter || !dateFilter.date) return responses;

    const { operator, date } = dateFilter;
    const filterTime = new Date(date).setHours(0, 0, 0, 0);

    return responses.filter((resp) => {
      if (!resp.submitted_at) return false;
      const respTime = new Date(resp.submitted_at).setHours(0, 0, 0, 0);

      switch (operator) {
        case 'on':
          return respTime === filterTime;
        case 'after':
          return respTime > filterTime;
        case 'before':
          return respTime < filterTime;
        default:
          return true;
      }
    });
  }, [responses, dateFilter]);

  const columns = useMemo(
    () => [
      {
        title: 'Template Name',
        dataIndex: 'template_name',
        key: 'template_name',
        sortable: true,
        filterable: true,
        filterType: 'text',
        width: 300,
        render: (name) => (
          <Space>
            <FileTextOutlined style={{ color: '#8B5CF6' }} />
            <span style={{ fontWeight: 500 }}>{name}</span>
          </Space>
        ),
      },
      {
        title: 'Type',
        dataIndex: 'template_type',
        key: 'template_type',
        width: 120,
        sortable: true,
        filterable: true,
        filterType: 'select',
        filterOptions: [
          { label: 'RFP', value: 'rfp' },
          { label: 'RFI', value: 'rfi' },
          { label: 'RFQ', value: 'rfq' },
          { label: 'Survey', value: 'survey' },
          { label: 'Assessment', value: 'assessment' },
        ],
        render: (type) => (
          <StatusTag
            status={type}
            color={
              type === 'rfp'
                ? 'purple'
                : type === 'rfi'
                ? 'blue'
                : type === 'rfq'
                ? 'cyan'
                : 'green'
            }
          >
            {type?.toUpperCase() || 'TEMPLATE'}
          </StatusTag>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        sortable: true,
        filterable: true,
        filterType: 'select',
        filterOptions: [
          { label: 'Submitted', value: 'submitted' },
          { label: 'Draft', value: 'draft' },
        ],
        render: (status) => (
          <StatusTag
            icon={status === 'submitted' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
            color={status === 'submitted' ? 'success' : 'warning'}
          >
            {status === 'submitted' ? 'Submitted' : 'Draft'}
          </StatusTag>
        ),
      },
      {
        title: 'Completion',
        dataIndex: 'completion_percentage',
        key: 'completion_percentage',
        width: 150,
        sortable: true,
        filterable: true,
        filterType: 'range',
        render: (percentage) => (
          <Progress
            percent={Math.round(percentage || 0)}
            size="small"
            status={percentage === 100 ? 'success' : 'active'}
          />
        ),
      },
      {
        title: 'Submitted By',
        dataIndex: 'submitted_by_name',
        key: 'submitted_by_name',
        width: 150,
        sortable: true,
        filterable: true,
        filterType: 'text',
        render: (name) => name || '—',
      },
      {
        title: 'Submitted',
        dataIndex: 'submitted_at',
        key: 'submitted_at',
        width: 150,
        sortable: true,
        render: (date) => (date ? formatDate(date) : 'Not submitted'),
      },
      {
        title: 'Last Updated',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 150,
        sortable: true,
        render: (date) => formatDate(date),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 130,
        fixed: 'right',
        render: (_, record) => (
          <Space size="small">
            <Tooltip title="View response">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/library/responses/${record.id}`)}
              >
                View
              </Button>
            </Tooltip>
          </Space>
        ),
      },
    ],
    [navigate]
  );

  const handleClearFilters = () => {
    setDateFilter(null);
  };

  const content = (
    <TGDataTable
      columns={columns}
      data={filteredResponses}
      loading={isLoading}
      showExport
      showColumnConfig
      exportFilename="completed-responses"
      presetKey="library.completed"
      externalFilterCount={dateFilter && dateFilter.date ? 1 : 0}
      onClearExternalFilters={handleClearFilters}
      toolbar={
        <SubmittedDateFilterBar
          dateFilter={dateFilter}
          onDateFilterChange={setDateFilter}
        />
      }
      rowKey="id"
      pagination={{
        pageSize: 20,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} responses`,
      }}
      scroll={{ x: 1500 }}
    />
  );

  if (embedded) {
    return <div style={{ padding: '24px' }}>{content}</div>;
  }

  return (
    <LibraryPage
      title="Completed Responses"
      breadcrumbs={[
        { label: 'Library', path: '/library' },
        { label: 'Completed' },
      ]}
    >
      {content}
    </LibraryPage>
  );
}
